<template>
  <v-dialog v-model="show" transition="dialog-bottom-transition" max-width="700" persistent>
    <v-card>
      <v-toolbar>
        <v-toolbar-title>{{ $t('confr.personal.title.form') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-alert type="warning" border="left" outlined
            class="mt-4 no-write-perm" v-if="!canWrite">
            {{ $t('confr.personal.settings.error.noPerm') }}
          </v-alert>
          <v-switch class="pcs-activate" v-model="activated"
            :disabled="!canWrite"
            :label="$t('confr.personal.settings.label.activate')">
          </v-switch>
          <fieldset class="pt-4 rounded-lg">
            <legend>{{ $t('confr.personal.title.data') }}</legend>
            <v-row>
              <v-col md="4" offset-md="2">
                <v-text-field v-model="pin" class="pcs-user-pin" required
                  outlined :rules="[ validPIN ]" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.userPin')">
                </v-text-field>
              </v-col>

              <v-col md="4">
                <v-text-field outlined class="pcs-max-users"
                  :rules="[ validMaxUsers ]" type="number"
                  v-model="maxUsers" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.maxUsers')">
                </v-text-field>
              </v-col>
            </v-row>
          </fieldset>

          <fieldset class="rounded-lg">
            <legend>{{ $t('confr.personal.title.opt') }}</legend>
            <v-row>
              <v-col md="4" offset-md="2">
                <v-switch class="pcs-count-say"
                  v-model="sayCount" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.countSay')">
                  <template v-slot:prepend>
                    <cp-help i18n="pcs.countSay"/>
                  </template>
                </v-switch>

                <v-switch class="pcs-moh" v-model="moh" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.moh')">
                  <template v-slot:prepend>
                    <cp-help i18n="pcs.moh"/>
                  </template>
                </v-switch>

                <v-switch class="pcs-wait-admin" v-model="waitAdmin" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.waitAdmin')">
                  <template v-slot:prepend>
                    <cp-help i18n="pcs.waitAdmin"/>
                  </template>
                </v-switch>
              </v-col>

              <v-col md="4">
                <v-switch class="pcs-join-say" v-model="sayJoin" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.joinSay')">
                  <template v-slot:prepend>
                    <cp-help i18n="pcs.joinSay"/>
                  </template>
                </v-switch>

                <v-switch class="pcs-open-menu" v-model="optMenu" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.openMenu')">
                  <template v-slot:prepend>
                    <cp-help i18n="pcs.openMenu"/>
                  </template>
                </v-switch>

                <v-switch class="pcs-listen-only" v-model="listenOnly" :disabled="!canUpdate"
                  :label="$t('confr.personal.settings.label.listenOnly')">
                  <template v-slot:prepend>
                    <cp-help i18n="pcs.listenOnly"/>
                  </template>
                </v-switch>
              </v-col>
            </v-row>
          </fieldset>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn text @click="cancel">
          {{ $t('confr.personal.settings.btn.cancel') }}
        </v-btn>
        <v-btn text :disabled="!readyToSave && !valid" @click="save">
          {{ $t('confr.personal.settings.btn.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import events from 'Store/events'
import API from '@/api'

export default {
  name: 'conference-personal-settings',
  data () {
    return {
      activated: false,
      pin: '',
      maxUsers: 0,
      sayCount: false,
      moh: false,
      waitAdmin: false,
      sayJoin: false,
      optMenu: false,
      listenOnly: false,
      valid: false
    }
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      conf: 'conferences/personal',
      canWrite: 'permissions/personalConfWrite'
    }),
    canUpdate: function () {
      return this.canWrite && this.activated
    },
    readyToSave: function () {
      return this.canWrite &&
        this.validPIN(this.pin) === true &&
        this.validMaxUsers(this.maxUsers) === true
    }
  },
  watch: {
    conf () {
      this.resetFromStore()
    },
    show (val) {
      if (val) {
        this.resetFromStore()
      }
    }
  },
  methods: {
    ...mapActions({
      reloadConf: 'conferences/init'
    }),
    save: function () {
      const config = this.configToPost()
      this.dbg('Update personal conference: %o', config)
      events.mask()
      API.updatePersonalConference(config).then(resp => {
        this.dbg('personal conference successfully update.')
        this.dbg('Respons object: %o', resp)
        this.$emit('close')
        this.reloadConf().then(() => {
          events.pubNotify({
            text: 'confr.personal.settings.saveOk',
            status: 'success'
          })
        })
      }).catch(err => {
        this.dbg(err)
        events.pubError('Failed to update personal conference')
      }).finally(() => {
        events.unmask()
      })
    },
    cancel: function () {
      this.resetFromStore()
      this.$emit('close')
    },
    resetFromStore: function () {
      if (!this.conf) {
        return
      }
      this.sayCount = false
      this.moh = false
      this.waitAdmin = false
      this.sayJoin = false
      this.optMenu = false
      this.listenOnly = false

      this.activated = !this.conf?.isDisabled
      this.pin = this.conf.pin
      this.maxUsers = this.conf.maxusers

      this.setupOpts(this.conf.opts)
    },
    validPIN: function (input) {
      const val = String(input)
      if (val.length > 2 && /^[0-9]+$/.test(val)) return true
      return this.$t('confr.personal.settings.error.invalPIN')
    },
    validMaxUsers: function (input) {
      const val = String(input)
      if (val.length === 0 || !/^-?[0-9]+$/.test(val)) {
        return this.$t('confr.personal.settings.error.maxUsersNum')
      }

      if (~~val < 0) {
        return this.$t('confr.personal.settings.error.maxUsersInval')
      }
      return true
    },
    setupOpts: function (input) {
      if ((typeof input !== 'string') || (input.trim().length === 0)) {
        return
      }

      for (const ch of input) {
        switch (ch) {
          case 'c': this.sayCount = true; break
          case 'M': this.moh = true; break
          case 'w': this.waitAdmin = true; break
          case 'I': this.sayJoin = true; break
          case 's': this.optMenu = true; break
          case 'l': this.listenOnly = true; break
        }
      }
    },
    configToPost: function () {
      const conf = {
        pin: this.pin,
        maxusers: this.maxUsers,
        disabled: this.activated ? 0 : 1,
        opts: ''
      }
      const opts = {
        sayCount: 'c',
        sayJoin: 'I',
        moh: 'M',
        optMenu: 's',
        waitAdmin: 'w',
        listenOnly: 'l'
      }

      for (const option in opts) {
        conf.opts += this[option] ? opts[option] : ''
      }

      return conf
    }
  }
}
</script>

<style scoped>
fieldset {
  aborder: 1px solid grey;
  a-moz-border-radius:6px;
  a-webkit-border-radius:6px;
  aborder-radius:6px;
}

fieldset legend {
  margin-left: 18px
}
</style>
